import { cn, ColorType, VariantClassesProps } from '../../utils'
import { ClassValue } from 'clsx'
import {
    ButtonShape,
    type ButtonSize,
    type ButtonVariant,
} from './Button.types'

// Main style getters
/** Styles for contained button variant */
const getContainedClasses = (color: ColorType, disabled?: boolean): string => {
    const colorClasses: VariantClassesProps = {
        primary: cn(
            'border text-background-base bg-background-primary border-background-primary hover:bg-background-hover-primary hover:border-background-hover-primary',
            disabled && '!bg-background-primary opacity-60'
        ),
        secondary:
            'border text-background-base border-background-secondary bg-background-secondary hover:bg-background-hover-secondary hover:border-background-hover-secondary',
        error: 'bg-background-error text-background-base',
        success: '!bg-success',
        warning: '!bg-warning',
        light: cn(
            'bg-background-base text-text-light border border-background-disabled hover:bg-background-hover-light hover:border-background-hover-light',
            disabled && 'opacity-60'
        ),
        dark: 'bg-background-dark border border-[hsl(var(--border-input))] hover:bg-background-hover-dark hover:border-background-hover-dark',
    }

    return colorClasses[color] || colorClasses.primary // Default color: primary
}

/* Styles for the after pseudo element for the link effect and animation */
const getHoverClasses = (...args: ClassValue[]): string =>
    'hover:text-opacity-80 disabled:text-color-disabled' + { ...args }

/** Styles for text button variant */
const getTextClasses = (color: ColorType): string => {
    const colorClasses: VariantClassesProps = {
        primary: getHoverClasses('active:text-opacity-80'),
        error: getHoverClasses('text-background-base'),
        success: getHoverClasses('!bg-primary-base  hover:text-success'),
        warning: getHoverClasses('hover:text-warning'),
        gradient: getHoverClasses('text-background-base'),
    }

    return colorClasses[color] || colorClasses.primary // Default color: primary
}

/* Styles for the after pseudo element for the link effect and animation */
const getAfterClasses = (...args: ClassValue[]): string =>
    'relative after:absolute after:h-0.5 after:w-0 after:bottom-0 after:left-0 over:after:w-full after:transition-all after:transition-all after:duration-300 cursor-pointer shadow-none border-none p-0' +
    { ...args }

/** Styles for link button variant */
const getLinkClasses = (color: ColorType): string => {
    const colorClasses: VariantClassesProps = {
        primary: getAfterClasses('after:bg-color-dark'),
        error: getAfterClasses('after:bg-error'),
        success: getAfterClasses('after:bg-success'),
        warning: getAfterClasses('after:!bg-warning'),
        gradient: getAfterClasses(
            'after:bg-gradient-to-r after:from-background-gradient-from after:to-background-gradient-to'
        ),
    }

    return colorClasses[color] || colorClasses.primary // Default color: primary
}

/** Base styles for the button */
export const getBaseClasses = (): string =>
    'transition font-normal flex shadow-light items-center relative flex justify-center items-center'

/** Size styles controller */
export const getSizeClasses = (
    size: ButtonSize,
    shape: ButtonShape,
    label: string
): string => {
    const shapeClasses: Record<ButtonShape, Record<string, string>> = {
        rounded: {
            lg: label ? 'px-9 py-3.5 gap-3' : 'gap-0 p-3.5',
            md: label ? 'px-7 py-3.5 gap-3' : 'gap-0 p-2.5',
            base: label ? 'px-5 py-2.5 gap-3' : 'gap-0 p-1.5',
            sm: label ? 'px-3.5 py-1 gap-3' : 'gap-0 p-0.5',
        },
        square: {
            lg: label ? 'px-7 py-3.5 gap-3' : 'gap-0 p-3.5',
            md: label ? 'px-6 py-3.5 gap-3' : 'gap-0 p-2.5',
            base: label ? 'px-3.5 py-2.5 gap-3' : 'gap-0 p-1.5',
            sm: label ? 'px-2.5 py-1 gap-3' : 'gap-0 p-0.5',
        },
        group: {
            lg: 'p-3.5',
            md: 'p-2.5',
            base: 'p-1.5',
            sm: 'py-1.5 px-2.5 flex items-center justify-center',
        },
        circle: {
            lg: 'p-3.5 rounded-full',
            md: 'px-5 py-4 rounded-full',
            base: 'p-1.5 rounded-full',
            sm: 'p-0.5 rounded-full',
        },
    }

    // Fallback to 'base' size if the size is not found
    return shapeClasses[shape]?.[size] || shapeClasses[shape]?.['base'] || ''
}

const getGroupClasses = (color: ColorType): string => {
    const colorClasses: VariantClassesProps = {
        primary:
            'border text-color-button bg-background-disabled border-background-primary hover:border-background-hover-primary',
        secondary:
            'border-none text-color-button border-background-secondary bg-background-secondary hover:border-background-hover-secondary',
        error: 'bg-background-error text-color-button',
        success: '!bg-success',
        warning: '!bg-warning',
        light: 'bg-button text-[hsl(var(--text-secondary))] border border border-[hsl(var(--background-neutral))] hover:border-background-hover-light',
        dark: 'bg-background-dark border border-none border-[hsl(var(--border-input))] hover:border-background-hover-dark',
        group: 'bg-background-base text-background-base border-none hover:bg-inactive/20 hover:border-bg-inactive/20 shadow-group rounded-none sm:last:rounded-r-lg last:rounded-r-none',
    }

    return colorClasses[color] || colorClasses.primary // Default color: primary
}

/** Variant styles controller */
export const getVariantClasses = (
    variant: ButtonVariant,
    color: ColorType,
    disabled?: boolean
): string => {
    const variantClasses: VariantClassesProps = {
        link: getLinkClasses(color),
        text: getTextClasses(color),
        contained: getContainedClasses(color, disabled),
        group: getGroupClasses(color),
    }

    return variantClasses[variant] || variantClasses.contained // Default variant: contained
}

export const getLabelStyle = (variant: ColorType, disabled?: boolean) => {
    const variantClasses: VariantClassesProps = {
        primary: cn(
            'text-color-button font-black',
            disabled && '!text-background-base opacity-80'
        ),
        secondary: 'text-color-button font-black',
        error: 'text-color-button font-black',
        light: 'text-color-title font-black',
        dark: 'text-color-button font-black',
    }

    return variantClasses[variant]
}
